import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { noop } from 'lodash'
import { Locale, Translation } from 'locale'
import { AxeActions } from 'api/AxeAction'
import { isNotNil } from 'utils/predicate'
import { DialogField, DialogRow } from 'view/components/inputs'
import { Groupbox } from 'view/components/panels'
import { DialogWindow } from 'view/components/panels/DialogWindow'
import { DefaultFundComboboxField, InstrumentComboboxField } from 'view/shared/entity'
import { createStructuredSelector } from 'reselect'
import { createCollateralsFromInstrumentIdGetterSelector } from 'selectors/instrumentSelectors'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { CollateralBasket, CollateralBasketType, EntityType, ProductSector } from 'api'
import { Nullable } from 'utils'
import { AxeDetail } from 'selectors/axeSelectors'
import { createPermissionedTradingEntitiesByInstrumentsAndTradingEntityRelationshipsGetterSelector } from 'selectors/tradingEntitySelectors'

interface AxeStageModalProps {
  onClose?: () => void
  axeDetails: AxeDetail[]
  stageAxe: typeof AxeActions.stage
  getCollateralsFromInstrumentId: ReturnType<ReturnType<typeof createCollateralsFromInstrumentIdGetterSelector>>
  getPermittedEntitiesByInstrument: ReturnType<
    ReturnType<typeof createPermissionedTradingEntitiesByInstrumentsAndTradingEntityRelationshipsGetterSelector>
  >
  getProductSector: (id: string) => Nullable<ProductSector>
}

const AxeStageComponent: React.FC<AxeStageModalProps> = ({
  onClose = noop,
  axeDetails,
  getCollateralsFromInstrumentId,
  getProductSector,
  getPermittedEntitiesByInstrument,
  stageAxe
}) => {
  const [{ fundId, instrumentId, collateralInstrumentIds, stagingCollateralBasket }, setState] = useState<{
    fundId?: string
    instrumentId?: string
    collateralInstrumentIds: string[]
    stagingCollateralBasket?: CollateralBasket
  }>({
    fundId: undefined,
    instrumentId: undefined,
    collateralInstrumentIds: [],
    stagingCollateralBasket: undefined
  })

  useEffect(() => {
    const collateralBasketCount = axeDetails.reduce((c, axeDetail) => {
      if (axeDetail.instrument) {
        const { collateralBasket } = getCollateralsFromInstrumentId(axeDetail.instrument?.id)
        return isNotNil(collateralBasket) ? (c += 1) : c
      }
      return c
    }, 0)

    if (collateralBasketCount > 1) {
      // stage with multiple collateral basket - error condition
      throw new Error('AxeStageModal: Cannot stage multiple axes with a collateral baskets')
    }

    if (collateralBasketCount === 1 && axeDetails.length > 1) {
      // stage with mixture of collateral and collateral basket - error condition
      throw new Error('AxeStageModal: Cannot stage multiple axes with mixture of collaterals and collateral baskets')
    }

    if (collateralBasketCount === 1 && axeDetails.length === 1) {
      // show the collateral dropdown
      const [axeDetail] = axeDetails
      if (axeDetail && isNotNil(axeDetail.instrument)) {
        const { collateralBasket } = getCollateralsFromInstrumentId(axeDetail.instrument.id)
        const isGCBasket =
          isNotNil(collateralBasket) && getProductSector(collateralBasket.productSectorId)?.basketType === CollateralBasketType.GC_BESPOKE
        setState(s => ({
          ...s,
          stagingCollateralBasket: isGCBasket ? collateralBasket : undefined,
          instrumentId: isGCBasket && isNotNil(collateralBasket.id) ? collateralBasket.id : undefined
        }))
      }
    }

    // trading entity dropdown is filtered by instrumentids
    setState(s => ({
      ...s,
      collateralInstrumentIds: stagingCollateralBasket
        ? [...stagingCollateralBasket.id, ...stagingCollateralBasket.collateralIds]
        : axeDetails.map(a => a.instrument?.id).filter(isNotNil)
    }))
  }, [axeDetails])

  const instrumentIdFilter = useCallback(
    (instrumentId: string) => {
      return stagingCollateralBasket ? [stagingCollateralBasket.id, ...stagingCollateralBasket.collateralIds].includes(instrumentId) : true
    },
    [stagingCollateralBasket]
  )

  const handleOnConfirm = () => {
    if (fundId) {
      axeDetails.forEach(axeDetail => {
        if (isNotNil(axeDetail.instrument)) {
          let collateral
          if (isNotNil(instrumentId)) {
            ;({ collateral } = getCollateralsFromInstrumentId(instrumentId))
          }
          stageAxe({
            axeId: axeDetail.axe.axeId,
            fundId,
            collateralId: isNotNil(collateral) ? collateral.id : null
          })
        }
      })
      onClose()
    }
  }

  const fundKeys = useMemo(() => {
    const initiatingEntityIds = axeDetails.reduce(
      (ids, { initiatorTradingEntity }) => (isNotNil(initiatorTradingEntity) ? [...ids, initiatorTradingEntity.id] : ids),
      []
    )
    return getPermittedEntitiesByInstrument(collateralInstrumentIds, initiatingEntityIds)
  }, [axeDetails, collateralInstrumentIds, getPermittedEntitiesByInstrument])

  return (
    <DialogWindow
      title={Translation.TC_AXE_STAGE_TITLE}
      titleArgs={{ multiple: axeDetails.length > 1 }}
      confirmButtonEnabled={isNotNil(fundId)}
      onConfirm={handleOnConfirm}
      confirmLabel={Translation.TC_BUTTON_OK}
      onClose={onClose}>
      <Groupbox
        title={Locale.translate(Translation.TC_AXE_STAGE_SELECT_FUND_COLLATERAL, {
          collateralEnabled: isNotNil(stagingCollateralBasket),
          multiple: axeDetails.length > 1
        })}>
        <DialogRow>
          <DialogField>
            <DefaultFundComboboxField
              label={Locale.translate(Translation.TC_FUND_LABEL)}
              placeholder={Locale.translate(Translation.SC_FUND_PLACEHOLDER)}
              noItemsText={Locale.translate(Translation.TC_AXE_STAGE_NO_TRADING_ENTITIES)}
              value={fundId}
              optionKeys={fundKeys}
              onUpdate={fundId => setState(s => ({ ...s, fundId }))}
              dataTestId={'AxeStageDialog-Fund'}
            />
          </DialogField>
        </DialogRow>
        {stagingCollateralBasket ? (
          <DialogRow>
            <DialogField>
              <InstrumentComboboxField
                label={Locale.translate(Translation.TC_INSTRUMENT_LABEL)}
                placeholder={Locale.translate(Translation.SC_INSTRUMENT_PLACEHOLDER)}
                value={instrumentId}
                fundId={fundId}
                instrumentIdFilter={instrumentIdFilter}
                onUpdate={selectedInstrumentId => setState(s => ({ ...s, selectedInstrumentId }))}
                dataTestId={'AxeStageDialog-Instrument'}
              />
            </DialogField>
          </DialogRow>
        ) : null}
      </Groupbox>
    </DialogWindow>
  )
}

export const AxeStageModal = connect(
  createStructuredSelector({
    getCollateralsFromInstrumentId: createCollateralsFromInstrumentIdGetterSelector(),
    getProductSector: createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
    getPermittedEntitiesByInstrument: createPermissionedTradingEntitiesByInstrumentsAndTradingEntityRelationshipsGetterSelector()
  }),
  {
    stageAxe: AxeActions.stage
  }
)(AxeStageComponent)
